import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, Send, Loader2, GraduationCap, Sparkles, Lightbulb, AlertTriangle, Check, Maximize2, Minimize2, Plus, MessageSquare, Trash2, Edit2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POS_KEY = "staff_assistant_fab_pos";
const ACTIVE_SESSION_KEY = "staff_assistant_active_session";

/**
 * Floating DoozyBot Training Assistant — ChatGPT-style, multi-session.
 * Draggable so it never obstructs the sidebar menu.
 *
 * Props:
 *   authAxios  — authenticated axios instance (cookie + X-Admin-Auth / X-Ops-Auth header)
 *   roleLabel  — optional display label under the header (e.g. "Ops Manager")
 */
const StaffAssistantWidget = ({ authAxios, roleLabel = "Staff" }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState("chat"); // chat | suggest | concern

  // ── Chat state (ChatGPT-style, backed by backend) ────────────────────
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const WELCOME = {
    role: "assistant",
    content: "Namaste! I'm DoozyBot — your internal coach, mentor & suggestion box. Ask me anything about work, SOPs, training… or use the tabs above to share an idea or raise a concern. 💪",
  };

  // ── Session CRUD ─────────────────────────────────────────────────────
  const refreshSessions = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/training/assistant/sessions`);
      return r.data.sessions || [];
    } catch { return []; }
  }, [authAxios]);

  const loadSession = useCallback(async (sid) => {
    if (!sid) return;
    setLoadingHistory(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/training/assistant/sessions/${sid}/messages`);
      const msgs = r.data.messages || [];
      setMessages(msgs.length > 0 ? msgs : [WELCOME]);
      setSessionId(sid);
      localStorage.setItem(ACTIVE_SESSION_KEY, sid);
    } catch {
      setMessages([WELCOME]);
    }
    setLoadingHistory(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios]);

  const createNewSession = useCallback(async () => {
    try {
      const r = await authAxios.post(`${API_URL}/api/training/assistant/sessions`);
      const sid = r.data.session_id;
      const list = await refreshSessions();
      setSessions(list);
      setMessages([WELCOME]);
      setSessionId(sid);
      localStorage.setItem(ACTIVE_SESSION_KEY, sid);
      setShowHistory(false);
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios, refreshSessions]);

  // Hydrate on first open — pick saved active session, else first, else create
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!open || hydratedRef.current || !authAxios) return;
    hydratedRef.current = true;
    (async () => {
      const list = await refreshSessions();
      setSessions(list);
      const saved = localStorage.getItem(ACTIVE_SESSION_KEY);
      const match = saved && list.find(s => s.session_id === saved);
      if (match) await loadSession(saved);
      else if (list.length > 0) await loadSession(list[0].session_id);
      else await createNewSession();
    })();
  }, [open, authAxios, refreshSessions, loadSession, createNewSession]);

  // ── Draggable FAB position (persist via localStorage) ─────────────────
  const [fabPos, setFabPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(POS_KEY) || "null");
      if (saved && typeof saved.x === "number" && typeof saved.y === "number") return saved;
    } catch { /* ignore */ }
    return { x: (typeof window !== "undefined" && window.innerWidth >= 1024) ? 288 : 24, y: 24 };
  });
  const fabPosRef = useRef(fabPos);
  useEffect(() => { fabPosRef.current = fabPos; }, [fabPos]);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ pointerX: 0, pointerY: 0, startX: 0, startY: 0, moved: false });

  const clampToViewport = useCallback((x, y) => {
    const fabSize = 60;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return {
      x: Math.max(8, Math.min(vw - fabSize - 8, x)),
      y: Math.max(8, Math.min(vh - fabSize - 8, y)),
    };
  }, []);

  const handlePointerMoveRef = useRef(null);
  const handlePointerUpRef = useRef(null);

  handlePointerMoveRef.current = (e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - dragStartRef.current.pointerX;
    const dy = e.clientY - dragStartRef.current.pointerY;
    if (Math.abs(dx) + Math.abs(dy) > 4) dragStartRef.current.moved = true;
    const vh = window.innerHeight;
    const newX = dragStartRef.current.startX + dx;
    const newY = dragStartRef.current.startY - dy;
    const clamped = clampToViewport(newX, vh - newY - 60);
    const next = { x: clamped.x, y: vh - clamped.y - 60 };
    fabPosRef.current = next;
    setFabPos(next);
  };

  handlePointerUpRef.current = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    window.removeEventListener("pointermove", onPointerMoveStable);
    window.removeEventListener("pointerup", onPointerUpStable);
    try { localStorage.setItem(POS_KEY, JSON.stringify(fabPosRef.current)); } catch { /* ignore */ }
  };

  const onPointerMoveStable = useCallback((e) => handlePointerMoveRef.current(e), []);
  const onPointerUpStable = useCallback(() => handlePointerUpRef.current(), []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    dragStartRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      startX: fabPosRef.current.x,
      startY: fabPosRef.current.y,
      moved: false,
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    window.addEventListener("pointermove", onPointerMoveStable);
    window.addEventListener("pointerup", onPointerUpStable);
  };

  const handleFabClick = () => {
    if (dragStartRef.current.moved) {
      dragStartRef.current.moved = false;
      return;
    }
    setOpen((p) => !p);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, tab]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending || !sessionId) return;
    const isFirstMsg = messages.length <= 1; // welcome message is the only one
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await authAxios.post(`${API_URL}/api/training/assistant/chat`, {
        message: msg,
        session_id: sessionId,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply || "I couldn't find anything for that yet." }]);
      if (isFirstMsg) {
        const list = await refreshSessions();
        setSessions(list);
      } else {
        setSessions(prev => {
          const idx = prev.findIndex(s => s.session_id === sessionId);
          if (idx === -1) return prev;
          const copy = [...prev];
          const [item] = copy.splice(idx, 1);
          return [{ ...item, updated_at: new Date().toISOString() }, ...copy];
        });
      }
    } catch {
      setMessages((m) => [...m, {
        role: "assistant",
        content: "Sorry — I'm having trouble right now. Please try again in a minute or file a training request from the Admin portal.",
      }]);
    }
    setSending(false);
  };

  const renameSession = async (sid) => {
    const title = renameValue.trim();
    if (!title) { setRenamingId(null); return; }
    try {
      await authAxios.put(`${API_URL}/api/training/assistant/sessions/${sid}`, { title });
      setSessions(prev => prev.map(s => s.session_id === sid ? { ...s, title } : s));
    } catch { /* ignore */ }
    setRenamingId(null); setRenameValue("");
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this chat permanently?")) return;
    try {
      await authAxios.delete(`${API_URL}/api/training/assistant/sessions/${sid}`);
      const list = await refreshSessions();
      setSessions(list);
      if (sid === sessionId) {
        if (list.length > 0) await loadSession(list[0].session_id);
        else await createNewSession();
      }
    } catch { /* ignore */ }
  };

  const quickQuestions = [
    "How do I handle a tricky customer?",
    "What's the SOP for booking handoff?",
    "Guide me through a Classic setup",
    "What to do if payment fails?",
  ];

  return (
    <>
      {/* Chat Window */}
      {open && (
        <div
          className={
            expanded
              ? "fixed inset-4 sm:inset-6 z-[9998]"
              : "fixed z-[9998] w-[380px] max-w-[calc(100vw-32px)]"
          }
          style={expanded ? { animation: "saSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)" } : {
            animation: "saSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
            left: `${fabPos.x}px`,
            bottom: `${fabPos.y + 80}px`,
          }}
          data-testid="staff-assistant-window"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex h-full"
            style={expanded ? { maxWidth: "1200px", margin: "0 auto" } : { height: "560px" }}
          >
            {/* Sidebar (only in expanded mode) */}
            {expanded && tab === "chat" && (
              <aside className="w-[240px] flex-shrink-0 border-r border-gray-100 bg-[#FAFAFB] flex flex-col" data-testid="staff-assistant-sidebar">
                <div className="p-3 border-b border-gray-100">
                  <button
                    onClick={createNewSession}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#E83E8C] text-white text-xs font-bold hover:bg-[#c22d78] transition-colors"
                    data-testid="staff-assistant-new-chat"
                  >
                    <Plus size={12} /> New chat
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                  {sessions.length === 0 && (
                    <p className="text-[11px] text-gray-400 text-center mt-6">No chats yet</p>
                  )}
                  {sessions.map(s => (
                    <SessionRow
                      key={s.session_id}
                      sess={s}
                      active={s.session_id === sessionId}
                      renaming={renamingId === s.session_id}
                      renameValue={renameValue}
                      onSelect={() => { loadSession(s.session_id); }}
                      onStartRename={() => { setRenamingId(s.session_id); setRenameValue(s.title); }}
                      onRenameChange={setRenameValue}
                      onRenameCommit={() => renameSession(s.session_id)}
                      onRenameCancel={() => { setRenamingId(null); setRenameValue(""); }}
                      onDelete={() => deleteSession(s.session_id)}
                    />
                  ))}
                </div>
              </aside>
            )}

            <div className="flex-1 flex flex-col h-full min-w-0">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1E1B4B] to-[#E83E8C] px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={20} className="text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-bold flex items-center gap-1.5">
                    DoozyBot <Sparkles size={12} className="text-[#F0DC00]" />
                  </p>
                  <p className="text-white/70 text-[10px] font-medium truncate">
                    Your Coach & Guide · {roleLabel}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {tab === "chat" && (
                  <>
                    <button
                      onClick={createNewSession}
                      className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all"
                      title="New chat"
                      data-testid="staff-assistant-new-chat-header"
                    >
                      <Plus size={16} />
                    </button>
                    {!expanded && (
                      <button
                        onClick={() => setShowHistory(p => !p)}
                        className={`relative text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all ${showHistory ? "bg-white/20" : ""}`}
                        title="Chat history"
                        data-testid="staff-assistant-history-toggle"
                      >
                        <MessageSquare size={16} />
                      </button>
                    )}
                  </>
                )}
                <button
                  onClick={() => setExpanded((p) => !p)}
                  className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all"
                  title={expanded ? "Collapse" : "Expand to full screen"}
                  data-testid="staff-assistant-expand"
                >
                  {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button
                  onClick={() => { setOpen(false); setExpanded(false); setShowHistory(false); }}
                  className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all"
                  data-testid="staff-assistant-close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Collapsed-mode History dropdown */}
            {!expanded && tab === "chat" && showHistory && (
              <div className="border-b border-gray-100 bg-[#FAFAFB] max-h-[200px] overflow-y-auto" data-testid="staff-assistant-history-dropdown">
                {sessions.length === 0 ? (
                  <p className="text-[11px] text-gray-400 text-center py-4">No chats yet</p>
                ) : (
                  <div className="p-2 space-y-1">
                    {sessions.map(s => (
                      <SessionRow
                        key={s.session_id}
                        sess={s}
                        active={s.session_id === sessionId}
                        renaming={renamingId === s.session_id}
                        renameValue={renameValue}
                        onSelect={() => { loadSession(s.session_id); setShowHistory(false); }}
                        onStartRename={() => { setRenamingId(s.session_id); setRenameValue(s.title); }}
                        onRenameChange={setRenameValue}
                        onRenameCommit={() => renameSession(s.session_id)}
                        onRenameCancel={() => { setRenamingId(null); setRenameValue(""); }}
                        onDelete={() => deleteSession(s.session_id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab switcher */}
            <div className="flex border-b border-gray-100 bg-white flex-shrink-0">
              <TabButton active={tab === "chat"} onClick={() => setTab("chat")} icon={<Sparkles size={12} />} label="Chat" testid="sa-tab-chat" />
              <TabButton active={tab === "suggest"} onClick={() => setTab("suggest")} icon={<Lightbulb size={12} />} label="Suggest" testid="sa-tab-suggest" />
              <TabButton active={tab === "concern"} onClick={() => setTab("concern")} icon={<AlertTriangle size={12} />} label="Concern" testid="sa-tab-concern" />
            </div>

            {tab === "suggest" ? (
              <SuggestForm authAxios={authAxios} onDone={() => setTab("chat")} />
            ) : tab === "concern" ? (
              <ConcernForm authAxios={authAxios} onDone={() => setTab("chat")} />
            ) : (
            <>
            {/* Messages */}
            <div
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFB]"
              data-testid="staff-assistant-messages"
            >
              {loadingHistory ? (
                <div className="flex justify-center items-center h-full">
                  <Loader2 size={18} className="animate-spin text-[#E83E8C]" />
                </div>
              ) : (
                <>
                  {messages.map((m, i) => (
                    <div
                      key={`${m.role}-${i}-${(m.content || "").slice(0, 12)}`}
                      className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[82%] px-3.5 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${
                          m.role === "user"
                            ? "bg-[#E83E8C] text-white rounded-2xl rounded-br-md shadow-sm"
                            : "bg-white text-[#1a1a1a] rounded-2xl rounded-bl-md shadow-sm border border-gray-100"
                        }`}
                      >
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex justify-start">
                      <div className="bg-white rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm border border-gray-100 flex items-center gap-2">
                        <Loader2 size={12} className="animate-spin text-[#E83E8C]" />
                        <span className="text-[11px] text-gray-400">thinking…</span>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Quick Questions */}
            {!loadingHistory && messages.length <= 1 && (
              <div className="px-3 pb-2 pt-2 flex gap-1.5 overflow-x-auto flex-shrink-0 bg-[#FAFAFB] border-t border-gray-100">
                {quickQuestions.map((q) => (
                  <button
                    key={q}
                    onClick={() => setInput(q)}
                    className="flex-shrink-0 px-3 py-1.5 rounded-full bg-[#E83E8C]/10 text-[#E83E8C] text-[11px] font-semibold hover:bg-[#E83E8C]/20 transition-colors whitespace-nowrap"
                    data-testid={`staff-assistant-quick-${q.slice(0, 10)}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="px-3 py-3 border-t border-gray-200 bg-white flex items-center gap-2.5 flex-shrink-0">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about training, SOPs, procedures…"
                disabled={sending || !sessionId}
                className="flex-1 text-sm px-4 py-2.5 rounded-full bg-[#F0F0F0] border border-gray-200 outline-none text-[#1a1a1a] placeholder-[#888] focus:border-[#E83E8C]/40 focus:ring-1 focus:ring-[#E83E8C]/20 transition-all"
                data-testid="staff-assistant-input"
              />
              <button
                onClick={send}
                disabled={!input.trim() || sending || !sessionId}
                className="w-10 h-10 rounded-full bg-[#E83E8C] flex items-center justify-center text-white disabled:opacity-30 hover:bg-[#c22d78] transition-colors flex-shrink-0 shadow-sm"
                data-testid="staff-assistant-send"
              >
                <Send size={15} />
              </button>
            </div>
            </>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Floating Button — DRAGGABLE */}
      <div
        className="fixed z-[9999]"
        style={{ left: `${fabPos.x}px`, bottom: `${fabPos.y}px`, touchAction: "none" }}
        data-testid="staff-assistant-fab-wrapper"
      >
        <button
          onPointerDown={onPointerDown}
          onClick={handleFabClick}
          className="relative w-[60px] h-[60px] rounded-full flex items-center justify-center shadow-xl hover:scale-110 active:scale-95 transition-transform cursor-grab active:cursor-grabbing"
          style={{
            background: "linear-gradient(135deg, #1E1B4B 0%, #E83E8C 100%)",
            touchAction: "none",
          }}
          data-testid="staff-assistant-fab"
          title="Drag to move · Click to open DoozyBot"
        >
          {open ? (
            <X size={24} className="text-white" />
          ) : (
            <>
              <GraduationCap size={26} className="text-white" />
              <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#10B981] border-2 border-white" />
            </>
          )}
        </button>
        {!open && (
          <span className="absolute top-1/2 -translate-y-1/2 left-[70px] whitespace-nowrap bg-[#1E1B4B] text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg pointer-events-none opacity-0 animate-[saPulseIn_0.6s_1.8s_forwards]">
            Ask DoozyBot ✨
          </span>
        )}
      </div>

      <style>{`
        @keyframes saSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes saPulseIn {
          0% { opacity: 0; transform: translateY(-50%) translateX(-6px); }
          100% { opacity: 1; transform: translateY(-50%) translateX(0); }
        }
      `}</style>
    </>
  );
};

export default StaffAssistantWidget;

// ─────────────────────────────────────────────────────────────
// Session row (ChatGPT-style)
// ─────────────────────────────────────────────────────────────
const SessionRow = ({ sess, active, renaming, renameValue, onSelect, onStartRename, onRenameChange, onRenameCommit, onRenameCancel, onDelete }) => (
  <div
    className={`group flex items-center gap-1 px-2 py-1.5 rounded-lg cursor-pointer transition-all ${
      active ? "bg-[#E83E8C]/10 border border-[#E83E8C]/30" : "hover:bg-gray-100 border border-transparent"
    }`}
    data-testid={`staff-assistant-session-${sess.session_id}`}
  >
    {renaming ? (
      <input
        autoFocus
        value={renameValue}
        onChange={(e) => onRenameChange(e.target.value)}
        onBlur={onRenameCommit}
        onKeyDown={(e) => {
          if (e.key === "Enter") onRenameCommit();
          if (e.key === "Escape") onRenameCancel();
        }}
        className="flex-1 min-w-0 px-1.5 py-0.5 text-[11px] rounded border border-[#E83E8C]/50 outline-none bg-white"
        data-testid="staff-assistant-rename-input"
      />
    ) : (
      <button
        onClick={onSelect}
        className={`flex-1 min-w-0 text-left text-[11px] font-medium truncate ${active ? "text-[#E83E8C]" : "text-[#1E1B4B]"}`}
        title={sess.title}
      >
        {sess.title}
      </button>
    )}
    {!renaming && (
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onStartRename(); }}
          className="p-1 rounded hover:bg-white"
          title="Rename"
          data-testid={`staff-assistant-rename-${sess.session_id}`}
        >
          <Edit2 size={10} className="text-gray-400" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1 rounded hover:bg-red-50"
          title="Delete"
          data-testid={`staff-assistant-delete-${sess.session_id}`}
        >
          <Trash2 size={10} className="text-red-400" />
        </button>
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────
// Tab button
// ─────────────────────────────────────────────────────────────
const TabButton = ({ active, onClick, icon, label, testid }) => (
  <button
    onClick={onClick}
    className={`flex-1 py-2.5 flex items-center justify-center gap-1.5 text-[11px] font-semibold transition-all ${
      active ? "text-[#E83E8C] border-b-2 border-[#E83E8C]" : "text-gray-400 hover:text-gray-600"
    }`}
    data-testid={testid}
  >
    {icon} {label}
  </button>
);

// ─────────────────────────────────────────────────────────────
// Suggest Form
// ─────────────────────────────────────────────────────────────
const SuggestForm = ({ authAxios, onDone }) => {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [category, setCategory] = useState("general");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await authAxios.post(`${API_URL}/api/doozybot/suggestion`, { title, details, category });
      setSuccess(data);
      setTitle(""); setDetails("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to submit");
    }
    setBusy(false);
  };

  if (success) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-5 text-center bg-[#FAFAFB]" data-testid="suggest-success">
        <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center mb-3">
          <Check size={22} className="text-white" />
        </div>
        <p className="text-sm font-bold text-[#1E1B4B]">Thanks for speaking up!</p>
        <p className="text-[11px] text-gray-500 mt-1 max-w-xs">
          <span className="font-mono">{success.suggestion_id}</span><br />
          Leadership has been notified. If your idea gets implemented, you'll receive a WhatsApp + email thank-you note.
        </p>
        <button
          onClick={() => { setSuccess(null); onDone && onDone(); }}
          className="mt-4 px-4 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-bold"
          data-testid="suggest-back"
        >
          Back to chat
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-2.5 bg-[#FAFAFB]" data-testid="suggest-form">
      <p className="text-[11px] text-gray-500">
        Got an idea to make DoozyGo better? Share it. Every suggestion gets read by Sumit — good ones get implemented and you'll be personally thanked.
      </p>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (short and punchy)"
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#E83E8C]"
        data-testid="suggest-title"
      />
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl"
        data-testid="suggest-category"
      >
        <option value="general">General</option>
        <option value="customer_experience">Customer experience</option>
        <option value="ops_efficiency">Ops efficiency</option>
        <option value="product_feature">Product / feature</option>
        <option value="tooling">Internal tool</option>
        <option value="culture">Culture / team</option>
      </select>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder="Tell us about your idea — what problem does it solve, and how?"
        rows={5}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#E83E8C] resize-none"
        data-testid="suggest-details"
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
      <button
        onClick={submit}
        disabled={busy || title.length < 5 || details.length < 10}
        className="w-full py-2 rounded-xl bg-[#E83E8C] text-white text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="suggest-submit"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Lightbulb size={14} />}
        {busy ? "Sending…" : "Send suggestion"}
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Concern Form
// ─────────────────────────────────────────────────────────────
const ConcernForm = ({ authAxios, onDone }) => {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [severity, setSeverity] = useState("normal");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await authAxios.post(`${API_URL}/api/doozybot/concern`, {
        title, details, severity, anonymous,
      });
      setSuccess(data);
      setTitle(""); setDetails("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to submit");
    }
    setBusy(false);
  };

  if (success) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-5 text-center bg-[#FAFAFB]" data-testid="concern-success">
        <div className="w-14 h-14 rounded-full bg-amber-500 flex items-center justify-center mb-3">
          <AlertTriangle size={22} className="text-white" />
        </div>
        <p className="text-sm font-bold text-[#1E1B4B]">Your concern has been recorded</p>
        <p className="text-[11px] text-gray-500 mt-1 max-w-xs">
          <span className="font-mono">{success.concern_id}</span><br />
          {success.severity === "high" || success.severity === "urgent"
            ? "Leadership has been alerted immediately. Someone will reach out."
            : "Leadership will review it within 48 hours."}
        </p>
        <button onClick={() => { setSuccess(null); onDone && onDone(); }} className="mt-4 px-4 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-bold" data-testid="concern-back">
          Back to chat
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-2.5 bg-[#FAFAFB]" data-testid="concern-form">
      <p className="text-[11px] text-gray-500">
        Raise a concern about work, ethics, a team member, a process — anything. It goes directly to leadership. You can submit anonymously if you prefer.
      </p>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (short — what's the issue?)"
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#E83E8C]"
        data-testid="concern-title"
      />
      <select
        value={severity}
        onChange={(e) => setSeverity(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl"
        data-testid="concern-severity"
      >
        <option value="low">Low — FYI</option>
        <option value="normal">Normal — needs attention</option>
        <option value="high">High — hurting team / work</option>
        <option value="urgent">Urgent — immediate danger or harm</option>
      </select>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder="What happened? Be as specific as you're comfortable with — dates, people, context."
        rows={5}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#E83E8C] resize-none"
        data-testid="concern-details"
      />
      <label className="flex items-center gap-2 text-[11px] text-gray-600 cursor-pointer">
        <input
          type="checkbox"
          checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)}
          className="accent-[#E83E8C]"
          data-testid="concern-anon"
        />
        Submit anonymously — leadership will see this as "(anonymous)"
      </label>
      {error && <p className="text-xs text-red-500">{error}</p>}
      <button
        onClick={submit}
        disabled={busy || title.length < 5 || details.length < 10}
        className="w-full py-2 rounded-xl bg-[#DC283C] text-white text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="concern-submit"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
        {busy ? "Sending…" : "Raise concern"}
      </button>
    </div>
  );
};
